import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

interface VendorCorrectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: number;
  transaction: {
    description: string;
    amount?: number;
    date?: string;
  };
  originalExtraction: {
    vendorName?: string;
    productName?: string;
    mwstRate?: string;
    confidence?: number;
  };
  onSuccess?: () => void;
}

export function VendorCorrectionDialog({
  open,
  onOpenChange,
  companyId,
  transaction,
  originalExtraction,
  onSuccess,
}: VendorCorrectionDialogProps) {
  const [correctedVendorName, setCorrectedVendorName] = useState("");
  const [correctedProductName, setCorrectedProductName] = useState("");
  const [correctedMwstRate, setCorrectedMwstRate] = useState<string>("7.7");
  const [correctionType, setCorrectionType] =
    useState<string>("complete_override");
  const [correctionReason, setCorrectionReason] = useState("");

  const submitCorrectionMutation = trpc.learning.submitCorrection.useMutation({
    onSuccess: data => {
      toast.success(
        data.patternExtracted
          ? `Correction saved! Pattern #${data.patternId} learned for future matches.`
          : "Correction saved! Pattern will be extracted when similar corrections accumulate."
      );
      onOpenChange(false);
      resetForm();
      onSuccess?.();
    },
    onError: error => {
      toast.error(`Failed to save correction: ${error.message}`);
    },
  });

  const resetForm = () => {
    setCorrectedVendorName("");
    setCorrectedProductName("");
    setCorrectedMwstRate("7.7");
    setCorrectionType("complete_override");
    setCorrectionReason("");
  };

  const handleSubmit = () => {
    if (!correctedVendorName.trim()) {
      toast.error("Please enter the correct vendor name");
      return;
    }

    submitCorrectionMutation.mutate({
      companyId,
      transactionDescription: transaction.description,
      transactionAmount: transaction.amount
        ? Math.round(transaction.amount * 100)
        : undefined,
      transactionDate: transaction.date
        ? Math.floor(new Date(transaction.date).getTime() / 1000)
        : undefined,
      originalVendorName: originalExtraction.vendorName,
      originalProductName: originalExtraction.productName,
      originalMwstRate: originalExtraction.mwstRate,
      originalConfidence: originalExtraction.confidence,
      correctedVendorName: correctedVendorName.trim(),
      correctedProductName: correctedProductName.trim() || undefined,
      correctedMwstRate,
      correctionType: correctionType as any,
      correctionReason: correctionReason.trim() || undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Correct Vendor Identification</DialogTitle>
          <DialogDescription>
            Help improve future accuracy by correcting this vendor extraction.
            Your correction will be learned and applied to similar transactions.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Transaction Context */}
          <div className="rounded-lg bg-muted p-4 space-y-2">
            <h4 className="font-medium text-sm">Transaction</h4>
            <p className="text-sm text-muted-foreground">
              {transaction.description}
            </p>
            {transaction.amount && (
              <p className="text-sm">
                Amount:{" "}
                <span className="font-medium">
                  CHF {transaction.amount.toFixed(2)}
                </span>
              </p>
            )}
          </div>

          {/* Original Extraction */}
          <div className="rounded-lg border p-4 space-y-2">
            <h4 className="font-medium text-sm">AI Extraction (Original)</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-muted-foreground">Vendor:</span>{" "}
                <span className="font-medium">
                  {originalExtraction.vendorName || "Unknown"}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Product:</span>{" "}
                <span className="font-medium">
                  {originalExtraction.productName || "Unknown"}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">MWST Rate:</span>{" "}
                <span className="font-medium">
                  {originalExtraction.mwstRate || "7.7"}%
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Confidence:</span>{" "}
                <span className="font-medium">
                  {originalExtraction.confidence || 0}%
                </span>
              </div>
            </div>
          </div>

          {/* Correction Form */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="vendor-name">Correct Vendor Name *</Label>
              <Input
                id="vendor-name"
                placeholder="e.g., Migros, Coop, SBB"
                value={correctedVendorName}
                onChange={e => setCorrectedVendorName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="product-name">Correct Product/Service</Label>
              <Input
                id="product-name"
                placeholder="e.g., Office Supplies, Groceries, Train Ticket"
                value={correctedProductName}
                onChange={e => setCorrectedProductName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="mwst-rate">Correct MWST Rate</Label>
              <Select
                value={correctedMwstRate}
                onValueChange={setCorrectedMwstRate}
              >
                <SelectTrigger id="mwst-rate">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">0% (Exempt)</SelectItem>
                  <SelectItem value="2.5">
                    2.5% (Reduced - Accommodation)
                  </SelectItem>
                  <SelectItem value="3.7">
                    3.7% (Special - Food/Beverages)
                  </SelectItem>
                  <SelectItem value="7.7">7.7% (Standard)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="correction-type">Correction Type</Label>
              <Select value={correctionType} onValueChange={setCorrectionType}>
                <SelectTrigger id="correction-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="vendor_name">Vendor Name Only</SelectItem>
                  <SelectItem value="product_name">
                    Product Name Only
                  </SelectItem>
                  <SelectItem value="mwst_rate">MWST Rate Only</SelectItem>
                  <SelectItem value="complete_override">
                    Complete Override
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reason">Reason (Optional)</Label>
              <Textarea
                id="reason"
                placeholder="Explain why this correction is needed..."
                value={correctionReason}
                onChange={e => setCorrectionReason(e.target.value)}
                rows={3}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              submitCorrectionMutation.isPending || !correctedVendorName.trim()
            }
          >
            {submitCorrectionMutation.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Submit Correction
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
